import React, { useState, useEffect } from "react";
import {
  Box, TextField, Button, Typography, Divider, Card,
  Grid
} from "@mui/material";
import { db } from "../../firebase";
import { collection, getDocs, query, where, Timestamp, orderBy, onSnapshot } from "firebase/firestore";

function MasterSaleReport() {
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState({ trips: [], payments: [], products: [] });
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [payModes, setPayModes] = useState([]);
  const [expenseHeads, setExpenseHeads] = useState([]);
  const [staffAttendance, setStaffAttendance] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [sortBy, setSortBy] = useState("challan");

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const fetchExpenseHeads = async () => {
      const snap = await getDocs(collection(db, "expenseHeads"));
      setExpenseHeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchExpenseHeads();
  }, []);

  const loadData = async () => {
    const unsubCustomers = onSnapshot(collection(db, "customers"), snap => {
      setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubPayModes = onSnapshot(collection(db, "payment_modes"), snap => {
      setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubCustomers();
      unsubPayModes();
    };

  };

  const getCustName = (id) => customers.find(c => c.id === id)?.customerName || id || "Unknown";
  const getPayModeName = (id) => payModes.find(m => m.payMode === id)?.payMode;

  const fetchAllData = async () => {
    if (!fromDate || !toDate) return alert("Select date range");
    setLoading(true);

    try {
      const start = Timestamp.fromDate(new Date(fromDate + " 00:00:00"));
      const end = Timestamp.fromDate(new Date(toDate + " 23:59:59"));
      const qTrips = query(collection(db, "trips"), where("grossDate", ">=", start), where("grossDate", "<=", end), orderBy("challan", "asc"));
      const qPay = query(collection(db, "customer_history"), where("date", ">=", start), where("date", "<=", end), orderBy("date", "asc"));
      const qPurchases = query(collection(db, "purchases"), where("purchasedate", ">=", fromDate), where("purchasedate", "<=", toDate), orderBy("purchasedate", "desc"));
      const qDealerPay = query(collection(db, "dealerPayments"), where("date", ">=", start), where("date", "<=", end), orderBy("date", "desc"));
      const qOtherExpense = query(collection(db, "otherExpenses"), where("date", ">=", start), where("date", "<=", end));
      const qStaff = getDocs(collection(db, "staff"));
      const qAttendance = getDocs(collection(db, "staffattendance"));
      const qBolders = query(collection(db, "boldertrips"), where("date", ">=", fromDate), where("date", "<=", toDate));

      const [
        tripSnap, paySnap, purchaseSnap, dealerPaySnap,
        dealerSnap, expenseSnap, staffSnap, attendanceSnap, qBolderSnap
      ] = await Promise.all([
        getDocs(qTrips),
        getDocs(qPay),
        getDocs(qPurchases),
        getDocs(qDealerPay),
        getDocs(collection(db, "dealers")),
        getDocs(qOtherExpense),
        qStaff,
        qAttendance,
        getDocs(qBolders),
      ]);

      // 3. Process Dealers Map
      const dealerMap = {};
      dealerSnap.forEach(doc => { dealerMap[doc.id] = doc.data().dealerName; });

      const headMap = {}; // Replace or fetch this if needed

      const bolderTrips = qBolderSnap.docs.reduce((acc, doc) => {
        const d = doc.data();
        acc.push({
          id: doc.id,
          ...d,
        });
        return acc;
      }, []);

      const processedExpenses = expenseSnap.docs.reduce((acc, doc) => {
        const d = doc.data();
        const amt = Number(d.amount || 0);
        const headName = (typeof headMap !== 'undefined' && headMap[d.expenseHeadId]) || "General";

        acc.grandTotal += amt;
        acc.list.push({
          id: doc.id,
          ...d,
          headName,
          displayDate: d.date?.toDate().toLocaleDateString('en-GB')
        });
        return acc;
      }, { grandTotal: 0, list: [] });

      // 5. Process Trips
      const trips = tripSnap.docs
        .map(doc => {
          const tripData = { id: doc.id, ...doc.data() };

          const calculatedTripTotal = (tripData.products || [])
            .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

          return {
            ...tripData,
            calculatedTotal: calculatedTripTotal
          };
        })
        // .filter(trip => trip.calculatedTotal > 0)
        .sort((a, b) => {
          if (sortBy === "ticketNo") {
            return (
              parseInt(a.ticketNo || 0, 10) -
              parseInt(b.ticketNo || 0, 10)
            );
          }

          return (
            parseInt(a.challan || 0, 10) -
            parseInt(b.challan || 0, 10)
          );
        });

      // 6. Process Bolder Data (using the same tripSnap)
      const bolderData = tripSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(trip => trip.products?.some(p =>
          (p.productName || "").toLowerCase().includes("bolder")
        ));


      // 7. Process Payments
      const payments = paySnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(pData => !pData.tripId)
        .map(pData => ({
          ...pData,
          displayCustomer: pData.customerName || (typeof getCustName === 'function' ? getCustName(pData.customerId || pData.customer) : "Unknown"),
          displayMode: typeof getPayModeName === 'function' ? getPayModeName(pData.paymentMode || pData.payMode) : "N/A"
        }));

      // 8. Aggregated Products
      const productMap = {};
      trips.forEach(trip => {
        (trip.products || []).forEach(p => {
          const name = p.productName || "General";
          if (!productMap[name]) productMap[name] = { name, qty: 0, amount: 0, count: 0 };
          productMap[name].qty += Number(p.qty) || 0;
          productMap[name].amount += Number(p.amount) || 0;
          productMap[name].count += 1;
        });
      });

      // 9. Process Purchases & Dealer Payments
      const purchases = purchaseSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        displayDealer: dealerMap[doc.data().dealerId] || "Unknown Dealer",
        calculatedTotal: Number(doc.data().grandtotal || 0)
      }));

      const dealerPayments = dealerPaySnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        displayDealer: dealerMap[doc.data().dealerId] || doc.data().Dealer || "Unknown",
        displayMode: typeof getPayModeName === 'function' ? getPayModeName(doc.data().paymentMode || doc.data().payMode) : "N/A"
      }));

      const staffMap = {};
      staffSnap.forEach(doc => {
        staffMap[doc.id] = doc.data();
      });
      setStaffList(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const filteredAttendance = attendanceSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(a => a.date >= fromDate && a.date <= toDate);

      const salaryData = filteredAttendance.map(att => {
        const staff = staffMap[att.staffId] || {};
        const monthlySalary = Number(staff.payment || 0);
        const perDay = monthlySalary / 30;

        let earned = 0;
        if (att.status === "Present") earned = perDay;
        else if (att.status === "Half Day") earned = perDay / 2;
        else earned = 0;

        return {
          ...att,
          staffName: staff.name || att.staffName || "Unknown",
          dailySalary: earned
        };
      });

      const totalSalaryExpense = salaryData.reduce(
        (sum, s) => sum + s.dailySalary,
        0
      );


      // 10. Update State
      setData({
        trips,
        payments,
        purchases,
        dealerPayments,
        expenses: processedExpenses, // Added this back in
        products: Object.values(productMap),
        bolderData,
        staffAttendance: salaryData,
        totalSalaryExpense,
        bolderTrips
      });

    } catch (err) {
      console.error("Fetch Error:", err);
      alert("Error fetching data: " + err.message);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchAllData();
  }, [customers, payModes, sortBy]);


  // Stylings - Increased sizes slightly for clarity
  const cellStyle = { border: '1px solid #000', padding: '3px 6px', fontSize: '0.85rem', color: '#322626' };
  const headerStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: '#eeeeee', textAlign: 'center', fontSize: '0.9rem' };
  const nestedStyle = { ...cellStyle, border: 'none', fontSize: '0.75rem', padding: '2px 4px' };

  const paymentModeList = payModes.map(m => m.payMode); // ["Cash","PhonePe","RTGS"]

  const getPaymentByMode = (trip, mode) => {
    if (!trip.payments || trip.payments.length === 0) return 0;

    return trip.payments
      .filter(p => (p.mode) === mode)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  };

  // TOTAL PER PAYMENT MODE
  const getTotalByMode = (mode) => {
    return data.trips.reduce((sum, trip) => {
      if (!trip.payments) return sum;

      const modeTotal = trip.payments
        .filter(p => p.mode === mode)
        .reduce((s, p) => s + (Number(p.amount) || 0), 0);

      return sum + modeTotal;
    }, 0);
  };

  // TOTAL PAID
  const totalPaid = data.payments.reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0
  );

  const totalSale = data.trips.reduce(
    (sum, t) => sum + (Number(t.calculatedTotal) || 0),
    0
  );

  const totalPending = totalSale - totalPaid;

  const productTotals = data.products.reduce(
    (acc, p) => {
      acc.qty += Number(p.qty || 0);
      acc.amount += Number(p.amount || 0);
      acc.count += Number(p.count || 0);
      return acc;
    },
    { qty: 0, amount: 0, count: 0 }
  );

  const getTripPaid = (trip) => {
    return data.payments
      .filter(p => p.tripId === trip.id) // ensure you store tripId in payments
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  };

  const totalQty = data.trips.reduce((sum, trip) => sum + (trip.products || []).reduce((pSum, p) => pSum + (Number(p.qty) || 0), 0), 0);
  const totalTripPaid = data.trips.reduce((sum, t) => sum + (Number(t.paidAmount) || 0), 0);
  const totalCollectionHistory = data.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const bolderTotals = (data?.bolderTrips || []).reduce(
    (acc, item) => {
      acc.noOfTrips += Number(item.noOfTrips || 0);
      acc.totalBrass += Number(item.totalBrass || 0);
      return acc;
    },
    {
      count: data?.bolderTrips?.length || 0,
      noOfTrips: 0,
      totalBrass: 0,
    }
  );


  const purchaseSummary = (data.purchases || []).reduce((acc, p) => {
    acc.total += Number(p.calculatedTotal || 0);
    acc.count += 1;
    return acc;
  }, { total: 0, count: 0 });


  // ✅ INCOME
  const totalCollection = data.payments.reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0
  );

  const totalSaleAmount = data.trips.reduce(
    (sum, t) => sum + (Number(t.totalAmount) || 0),
    0
  );

  const totalIncome = totalSaleAmount + totalCollection;


  // ✅ EXPENSES
  const totalBolderExpense = bolderTotals.totalAmount || 0;
  console.log(totalBolderExpense);


  const totalPurchaseExpense = purchaseSummary.total || 0;

  const totalOtherExpense = data.expenses?.grandTotal || 0;

  const totalLightBill = 23333; // static (you can make dynamic later)

  const totalStaffSalary = data.totalSalaryExpense || 0;
  const royaltyRate = 660;

  const totalRoyaltyExpense = totalQty * royaltyRate;

  const totalExpense =
    totalBolderExpense +
    totalPurchaseExpense +
    totalOtherExpense +
    totalLightBill +
    totalRoyaltyExpense +   // ✅ added
    totalStaffSalary;

  const totalFixedOtherExpense = totalLightBill + totalRoyaltyExpense;
  const netProfit = totalIncome - totalExpense;


  return (
    <Box p={1} sx={{ bgcolor: "#fff" }}>
      {/* Filters */}
      <Card sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', borderRadius: '8px' }} className="no-print">
        <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
        <TextField
          select
          size="small"
          label="Sort By"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          SelectProps={{ native: true }}
        >
          <option value="challan">Challan No</option>
          <option value="ticketNo">Ticket No</option>
        </TextField>
        <Button variant="contained" onClick={fetchAllData} disabled={loading} sx={{ minWidth: '120px' }}>{loading ? "Fetching..." : "Generate"}</Button>
        <Button variant="outlined" onClick={() => window.print()}>Print PDF</Button>
      </Card>

      <Grid className="printable-area p-2">
        <Box sx={{ border: '2px solid #000' }}>
          <Box textAlign="center" mb={2}>
            {/* <Typography variant="h5" fontWeight="bold">TEJAS STONE CRUSHER</Typography>
            <Typography variant="body2">Varuda Road, Dist. Dharashiv, Maharashtra</Typography> */}
            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', letterSpacing: 1 }}>
              MASTER SALE & COLLECTION REPORT ({fromDate} TO {toDate})
            </Typography>
            <Divider sx={{ my: 2, bgcolor: '#000', height: '2px' }} />
          </Box>

          {/* SECTION 1: TRIPS */}
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: "green" }}> &nbsp;I. TRIP SALES LOG</Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, width: '20px' }}>Sr.</th>
                <th style={{ ...headerStyle, width: '60px' }}>Tkt</th>
                <th style={{ ...headerStyle, width: '140px' }}>Date</th>
                <th style={{ ...headerStyle, width: '40px' }}>Chalan No</th>
                <th style={{ ...headerStyle, width: '150px' }}>Customer</th>
                <th style={{ ...headerStyle, width: '150px' }}>Mobile</th>
                <th style={{ ...headerStyle, width: '100px' }}>Vehicle</th>
                <th style={{ ...headerStyle, width: '180px', textAlign: 'left' }}>Product Item</th>
                <th style={{ ...headerStyle, width: '60px' }}>Qty</th>
                <th style={{ ...headerStyle, width: '60px' }}>Rate</th>
                <th style={{ ...headerStyle, width: '100px' }}>Total</th>
                {paymentModeList.map((mode, i) => (
                  <th key={i} style={{ ...headerStyle, width: '90px' }}>{mode}</th>
                ))}

                <th style={{ ...headerStyle, width: '90px' }}>Paid</th>
                <th style={{ ...headerStyle, width: '90px' }}>Pending</th>
                <th style={{ ...headerStyle, width: '100px' }}>Net Weight</th>

              </tr>
            </thead>
            <tbody>
              {data.trips.map((row, index) => (
                <tr key={row.id}>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                  <td style={{ ...cellStyle }}><strong>{row.ticketNo}</strong></td>
                  <td style={{ ...cellStyle }}>
                    {row.grossDate ? (
                      row.grossDate.toDate().toLocaleString('en-GB', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', hour12: false
                      }).replace(/\//g, '/')
                    ) : "-"}
                  </td>
                  <td style={{ ...cellStyle }}><strong>{row.challan}</strong></td>
                  <td style={{ ...cellStyle }}><strong>{row.customerName}</strong></td>
                  <td style={{ ...cellStyle }}><strong>{row.mobile}</strong></td>
                  <td style={{ ...cellStyle, fontSize: '0.75rem' }}>{row.vehicleNo}</td>

                  {/* SEPARATED PRODUCT NAME COLUMN */}
                  <td style={{ ...cellStyle, padding: 0 }}>
                    {(row.products || []).map((p, i) => (
                      <div key={i} style={{
                        padding: '4px 8px',
                        borderBottom: i === row.products.length - 1 ? 'none' : '1px solid #ddd',
                        minHeight: '20px'
                      }}>
                        {p.productName}
                      </div>
                    ))}
                  </td>

                  {/* SEPARATED QTY COLUMN */}
                  <td style={{ ...cellStyle, padding: 0, textAlign: 'center' }}>
                    {(row.products || []).map((p, i) => (
                      <div key={i} style={{
                        padding: '4px 8px',
                        borderBottom: i === row.products.length - 1 ? 'none' : '1px solid #ddd',
                        fontWeight: '500',
                        minHeight: '20px'
                      }}>
                        {p.qty}
                      </div>
                    ))}
                  </td>

                  <td style={{ ...cellStyle, padding: 0, textAlign: 'center' }}>
                    {(row.products || []).map((p, i) => (
                      <div key={i} style={{
                        padding: '4px 8px',
                        borderBottom: i === row.products.length - 1 ? 'none' : '1px solid #ddd',
                        fontWeight: '500',
                        minHeight: '20px'
                      }}>
                        {p.rate}
                      </div>
                    ))}
                  </td>

                  <td style={{ ...cellStyle, textAlign: "right" }}>
                    ₹{Number(row.totalAmount || 0).toLocaleString()}
                  </td>

                  {paymentModeList.map((mode, i) => (
                    <td key={i} style={{ ...cellStyle, textAlign: "right" }}>
                      ₹{getPaymentByMode(row, mode)}
                    </td>
                  ))}

                  <td style={{ ...cellStyle, textAlign: "right", color: 'green' }}>
                    ₹{Number(row.paidAmount || 0).toLocaleString()}
                  </td>

                  <td style={{ ...cellStyle, textAlign: "right", color: "red", fontWeight: "bold" }}>
                    ₹{Number(row.remainingAmount || 0).toLocaleString()}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right", }}>
                    {row.netWeight }
                  </td>
                </tr>
              ))}

              <tr style={{ backgroundColor: '#eeeeee', fontWeight: 'bold' }}>
                {/* Span 6 covers: Sr, Date, Ticket, Customer, Vehicle, Product Name */}
                <td colSpan={8} style={{ ...cellStyle, textAlign: 'right', fontSize: '1rem' }}>
                  TOTAL SUMMARY
                </td>

                {/* Display Total Brass/Quantity */}
                <td style={{ ...cellStyle, textAlign: 'center' }}>
                  {totalQty.toFixed(2)}
                </td>
                <td style={{ ...cellStyle, textAlign: 'center' }}></td>

                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{data.trips.reduce((s, i) => s + (Number(i.totalAmount) || 0), 0).toLocaleString()}
                </td>

                {paymentModeList.map((mode, i) => (
                  <td key={i} style={{ ...cellStyle, textAlign: 'right' }}>
                    ₹{getTotalByMode(mode).toLocaleString()}
                  </td>
                ))}

                {/* Trip-specific Paid Total */}
                <td style={{ ...cellStyle, textAlign: 'right', color: 'green' }}>
                  ₹{totalTripPaid.toLocaleString()}
                </td>

                <td style={{ ...cellStyle, textAlign: 'right', color: 'red' }}>
                  ₹{(totalSale - totalTripPaid).toLocaleString()}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right'}}></td>
              </tr>
            </tbody>
          </table>
          {/* SECTION 2: PAYMENTS */}
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: "green" }}> &nbsp;II. CUSTOMER PREVIOUS OUTSTANDING COLLECTION </Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
            <thead>
              <tr>
                <th style={headerStyle}>Sr.</th>
                <th style={headerStyle}>Payment Date</th>
                <th style={headerStyle}>Customer Name</th>
                <th style={headerStyle}>Payment Mode</th>
                <th style={headerStyle}>Paid Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((row, index) => (
                <tr key={row.id}>
                  <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {row.date ? (
                      (() => {
                        const d = row.date.toDate();
                        const day = String(d.getDate()).padStart(2, '0');
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const year = d.getFullYear();
                        return `${day}-${month}-${year}`; // Change separators here if needed
                      })()
                    ) : "-"}
                  </td>
                  <td style={cellStyle}>{row.displayCustomer}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{row.displayMode}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                    ₹{(Number(row.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' }}>TOTAL COLLECTION</td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' }}>
                  ₹{data.payments.reduce((s, i) => s + (Number(i.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table>

          {/* SECTION 3: PRODUCT SUMMARY */}
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: "green" }}> &nbsp;III. PRODUCT WISE SUMMARY</Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerStyle}>Sr.</th>
                <th style={headerStyle}>Product Name</th>
                <th style={headerStyle}>Trip Count</th>
                <th style={headerStyle}>Total Quantity</th>
                <th style={headerStyle}>Total Sales Value</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((row, index) => (
                <tr key={index}>
                  <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                  <td style={cellStyle}>{row.name}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{row.count}</td>
                  <td style={{ ...cellStyle, textAlign: 'right' }}>{row.qty.toFixed(2)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                    ₹{row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
            <tr style={{ backgroundColor: '#f0f0f0' }}>
              <td style={{ ...cellStyle }}></td>
              <td style={{ ...cellStyle, fontWeight: 'bold' }}>TOTAL</td>
              <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold' }}>{productTotals.count}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>{productTotals.qty.toFixed(2)}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                ₹{productTotals.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </td>
            </tr>
          </table>

          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: "green" }}> &nbsp;IV. BOLDER TRIPS</Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerStyle}>Sr.</th>
                <th style={cellStyle}>Date</th> {/* Changed to th for consistency */}
                <th style={cellStyle}>Vehicle No</th>
                <th style={cellStyle}>Vehicle Vol</th>
                <th style={cellStyle}>No of Trips</th>
                <th style={cellStyle}>Total Brass</th>
              </tr>
            </thead>
            <tbody>
              {data?.bolderTrips?.map((row, index) => (
                <tr key={index}>
                  <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                  <td style={cellStyle}>{row.date}</td>
                  <td style={cellStyle}>{row.vehicleNo}</td>
                  <td style={cellStyle}>{row.vehicleVolume}</td>
                  <td style={cellStyle}>{row.noOfTrips}</td>
                  <td style={{ ...cellStyle, fontWeight: 'bold', color: '#1b5e20' }}>{row.totalBrass}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                <td colSpan={3} style={{ ...cellStyle, fontWeight: "bold", textAlign: "center", }}>TOTAL</td>
                <td style={{ ...cellStyle,  fontWeight: "bold", }}>{bolderTotals.count}</td>
                <td style={{ ...cellStyle,fontWeight: "bold",}}>{bolderTotals.noOfTrips}</td>

                <td
                  style={{
                    ...cellStyle,
                    fontWeight: "bold",
                    color: "#1b5e20",
                  }}
                >
                  {bolderTotals.totalBrass.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>


          {/* 
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mt: 3, mb: 1, color: "red" }}>&nbsp;
            IV. PURCHASE EXPENSE
          </Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerStyle}>Sr.</th>
                <th style={headerStyle}>Date</th>
                <th style={headerStyle}>Purchase No</th>
                <th style={headerStyle}>Dealer</th>
                <th style={headerStyle}>Purchase Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.purchases || []).map((row, index) => (
                <tr key={index}>
                  <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                  <td style={{ ...cellStyle, width: '120px', textAlign: 'center' }}>
                    {row.purchasedate}
                  </td>
                  <td style={{ ...cellStyle, width: '120px' }}>
                    <strong>{row.purchaseno}</strong>
                  </td>
                  <td style={cellStyle}>
                    <strong>{row.displayDealer}</strong>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                    ₹{(row.calculatedTotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}

              <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                <td colSpan={3} style={{ ...cellStyle, textAlign: 'right' }}>
                  TOTAL PURCHASES ({purchaseSummary.count}):
                </td>
                <td style={cellStyle}></td>
                <td style={{ ...cellStyle, textAlign: 'right', fontSize: '1rem', color: 'red' }}>
                  ₹{purchaseSummary.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table> */}

          {/* 
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mt: 3, mb: 1, color: "red" }}>&nbsp;
            V. OTHER EXPENSES
          </Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerStyle}>SR</th>
                <th style={headerStyle}>Expense Head</th>
                <th style={headerStyle}>Date</th>
                <th style={headerStyle}>Description</th>
                <th style={headerStyle}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(data.expenses?.list || []).map((row, index) => {
                const headName = row.headName ||
                  expenseHeads.find(h => h.id === row.expenseHeadId)?.name ||
                  row.expenseHeadId;

                return (
                  <tr key={row.id || index}>
                    <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                    <td style={cellStyle}>{headName}</td>
                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                      {row.displayDate || (row.date?.toDate ? row.date.toDate().toLocaleDateString('en-GB') : "-")}
                    </td>

                    <td style={cellStyle}>{row.description || "-"}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      ₹{Number(row.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}

              <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                <td style={cellStyle}></td>
                <td colSpan={3} style={{ ...cellStyle, textAlign: 'right' }}>TOTAL OTHER EXPENSES:</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: 'red' }}>
                  ₹{(data.expenses?.grandTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table> */}

          {/* <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mt: 3, mb: 1, color: "red" }}>&nbsp;
            VI. OTHER EXPENSES
          </Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerStyle}>SR</th>
                <th style={headerStyle}>Type</th>
                <th style={headerStyle}>Date</th>
                <th style={headerStyle}>Description</th>
                <th style={headerStyle}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>1</td>
                <td style={cellStyle}>Light Bill</td>
                <td style={{ ...cellStyle, textAlign: 'center' }}>
                  {new Date().toLocaleDateString('en-GB')}
                </td>
                <td style={cellStyle}>Daily Electricity Charges</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{Number(23333).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
              <tr>
                <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>1</td>
                <td style={cellStyle}>Royalty Expense</td>
                <td style={{ ...cellStyle, textAlign: 'center' }}>
                  {new Date().toLocaleDateString('en-GB')}
                </td>
                <td style={cellStyle}>Royalty Expense ( {totalQty.toFixed(2)} X 670)</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalRoyaltyExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                <td style={cellStyle}></td>
                <td colSpan={3} style={{ ...cellStyle, textAlign: 'right' }}>TOTAL OTHER EXPENSES:</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: 'red' }}>
                  ₹{totalFixedOtherExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table> */}


          {/* <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mt: 3, mb: 1, color: "red" }}>&nbsp;
            VII. STAFF SALARY (DAILY)
          </Typography>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerStyle}>SR</th>
                <th style={headerStyle}>Date</th>
                <th style={headerStyle}>Staff Name</th>
                <th style={headerStyle}>Status</th>
                <th style={headerStyle}>Daily Salary</th>
              </tr>
            </thead>
            <tbody>
              {(data.staffAttendance || []).map((row, index) => (
                <tr key={index}>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>

                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {row.date}
                  </td>

                  <td style={cellStyle}>{row.staffName}</td>

                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    {row.status}
                  </td>

                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                    ₹{Number(row.dailySalary || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))} 

              <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                <td colSpan={4} style={{ ...cellStyle, textAlign: 'right' }}>
                  TOTAL SALARY EXPENSE:
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', color: 'red' }}>
                  ₹{(data.totalSalaryExpense || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table> */}

          {/* <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mt: 2, mb: 1, color: "blue" }}>&nbsp;
            VIII. FINAL SUMMARY
          </Typography>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>

              <tr>
                <td style={cellStyle}><b>Total Sale</b></td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalSaleAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr>
                <td style={cellStyle}><b>Total Collection</b></td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalCollection.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr style={{ background: '#e3f2fd' }}>
                <td style={{ ...cellStyle, fontWeight: 'bold' }}>TOTAL INCOME</td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', color: 'green' }}>
                  ₹{totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr>
                <td style={cellStyle}>Bolder Expense</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalBolderExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr>
                <td style={cellStyle}>Purchase Expense</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalPurchaseExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr>
                <td style={cellStyle}>Other Expense</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalOtherExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr>
                <td style={cellStyle}>Light Bill</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalLightBill.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr>
                <td style={cellStyle}>Royalty Expense</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalRoyaltyExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
              <tr>
                <td style={cellStyle}>Staff Salary</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  ₹{totalStaffSalary.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr style={{ background: '#ffebee' }}>
                <td style={{ ...cellStyle, fontWeight: 'bold' }}>TOTAL EXPENSE</td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', color: 'red' }}>
                  ₹{totalExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

              <tr style={{ background: '#e8f5e9' }}>
                <td style={{ ...cellStyle, fontWeight: 'bold' }}>NET PROFIT / LOSS</td>
                <td style={{
                  ...cellStyle,
                  textAlign: 'right',
                  fontWeight: 'bold',
                  color: netProfit >= 0 ? 'green' : 'red'
                }}>
                  ₹{netProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>

            </tbody>
          </table> */}

        </Box>
      </Grid>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area, .printable-area * { visibility: visible; }
          .printable-area { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
            border: none !important; 
          }
          .no-print { display: none !important; }
          @page { size: A4; margin: 10mm;size: landscape; }
        }
      `}</style>
    </Box>
  );
}

export default MasterSaleReport;